import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { loadStripe } from '@stripe/stripe-js';
import { requestCode, signUp } from '../api';

const LoginDialog = ({ 
  open, 
  onClose, 
  onSuccess, 
  title = "To finalize this booking, please verify your mobile number",
  successCallback = null, // Optional callback for custom success handling
  initialPhoneNumber = '' // Optional initial phone number
}) => {
  const [step, setStep] = useState('phone'); // 'phone' or 'code'
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [code, setCode] = useState('');  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);  // Update phone number when initialPhoneNumber prop changes
  useEffect(() => {
    let processedPhoneNumber = initialPhoneNumber;
    
    // If phone number is exactly 10 digits (US format without country code), add US country code
    if (/^\d{10}$/.test(initialPhoneNumber)) {
      processedPhoneNumber = `1${initialPhoneNumber}`;
    }
    
    setPhoneNumber(processedPhoneNumber);
  }, [initialPhoneNumber]);
  const handlePhoneSubmit = async () => {
    setError('');
    setLoading(true);
    
    try {
      let processedPhoneNumber = phoneNumber;
      
      // If phone number is exactly 10 digits (US format without country code), add US country code
      if (/^\d{10}$/.test(phoneNumber)) {
        processedPhoneNumber = `1${phoneNumber}`;
        setPhoneNumber(processedPhoneNumber); // Update the display
      }
      
      const fullPhoneNumber = `+${processedPhoneNumber}`;
      const phoneNumberObject = parsePhoneNumberFromString(fullPhoneNumber);
      if (!phoneNumberObject) throw new Error('Invalid phone number');

      const localPhoneNumber = phoneNumberObject.nationalNumber;
      const countryCode = phoneNumberObject.countryCallingCode;

      localStorage.setItem('phone', localPhoneNumber);
      localStorage.setItem('country_code', countryCode);

      await requestCode(localPhoneNumber, countryCode);
      setStep('code');
    } catch (error) {
      setError('Failed to send SMS code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (!code.match(/^\d{4}$/)) {
      setError('Please enter a valid 4-digit code.');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const phone = localStorage.getItem('phone');
      const operatorString = localStorage.getItem('operator');
      const country_code = localStorage.getItem('country_code');
      const operator = JSON.parse(operatorString);
        if (!operator || !operator.stripe_pk) {
        console.error('Operator or stripe_pk is not defined.');
        setError('Operator information is missing. Please refresh the page.');
        setLoading(false);
        return;
      }

      const user = await signUp(country_code, phone, code, operator.id);
      const waiver_status = user.waiver_status;
      const user_id = user.id;
      const stripe_customer_id = user.stripe_customer_id;      // Store user information
      localStorage.setItem('user', JSON.stringify(user));      // Call success callback if provided, otherwise call default onSuccess
      try {
        if (successCallback) {
          await successCallback({
            user,
            user_id,
            waiver_status,
            stripe_customer_id,
            operator,
            loadStripe
          });
        } else {
          onSuccess(user);
        }
        
        // Close the dialog after successful authentication
        onClose();
        
        // Reset dialog state
        setStep('phone');
        setPhoneNumber(initialPhoneNumber);
        setCode('');
        setError('');
      } catch (callbackError) {
        console.error('Error in success callback:', callbackError);
        setError('Authentication successful, but there was an issue proceeding. Please try again.');
        // Don't return here - let the finally block handle setLoading(false)
      }
    } catch (error) {
      setError(`Failed to sign in. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    setStep('phone');
    setPhoneNumber(initialPhoneNumber);
    setCode('');
    setError('');
    onClose();
  };

  const renderPhoneStep = () => (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '16px', marginLeft: '15%' }}>
          <PhoneInput
            country={'us'}
            value={phoneNumber}
            onChange={setPhoneNumber}
            inputStyle={{ width: '50%' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handlePhoneSubmit();
              }
            }}
          />
        </div>
        {error && <Typography color="error" style={{ marginLeft: '33%' }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handlePhoneSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading}
          sx={{
            backgroundColor: '#00B894',
            color: 'white',
            borderRadius: '24px',
            height: '40px',
            marginLeft: '10%', 
            padding: '0 16px',
            '&:hover': {
              backgroundColor: '#00A983',
            },
          }}  
        >
          {loading ? 'Sending...' : 'NEXT'}
        </Button>
      </DialogActions>
    </>
  );

  const renderCodeStep = () => (
    <>
      <DialogTitle>Enter the code sent to your phone</DialogTitle>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '16px', marginLeft: '15%' }}>
        <DialogContent>
          <TextField
            label="SMS Code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleCodeSubmit();
              }
            }}
            error={!!error}
            helperText={error}
            sx={{ width: '30%' }}
          />
        </DialogContent>
      </div>
      <DialogActions>
        <Button 
          onClick={handleCodeSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading}
          sx={{
            backgroundColor: '#00B894',
            color: 'white',
            borderRadius: '24px',
            height: '40px',
            marginLeft: '10%', 
            padding: '0 16px',
            '&:hover': {
              backgroundColor: '#00A983',
            },
          }}
        >
          {loading ? 'Verifying...' : 'NEXT'}
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {step === 'phone' ? renderPhoneStep() : renderCodeStep()}
    </Dialog>
  );
};

export default LoginDialog;
